import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { clients as clientsApi } from "../api";
import MetricTile, { formatNumber } from "../components/MetricTile";
import MetricChart from "../components/MetricChart";
import DeviceBreakdown from "../components/DeviceBreakdown";
import NarrativeReader from "../components/NarrativeReader";

/**
 * Client-facing marketing page.
 *
 * Redesigned to lead with metrics, not prose. The layout:
 *   1. Four big KPI tiles (Visitors / Engaged / Inquiries / AI visibility)
 *   2. Small multiples — sessions by day, device split, top landing pages
 *   3. Narrative, collapsed by default — readable if you want depth, out
 *      of the way if you want the numbers.
 *
 * No raw metric names (impressions, CTR, etc.), no recommendations,
 * no agency-only language.
 */
export default function ClientMarketing() {
  const { clientId } = useParams();
  const id = parseInt(clientId, 10);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([
      clientsApi.overview(id).catch((e) => { throw e; }),
      clientsApi.narrative(id).catch(() => null),
      clientsApi.dataVisibility(id, 60).catch(() => ({ series: [] })),
    ]).then(([overview, narrative, vis]) => setData({ overview, narrative, visSeries: vis.series }))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [id]);

  if (err) return <div className="page"><div className="error-box">{err}</div></div>;
  if (!data) return <div className="page"><p className="subtle">Loading your report…</p></div>;

  const { overview, narrative, visSeries } = data;
  const periodLabel = new Date(overview.period.start).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  // --- Compose metric-tile figures from the overview payload ---
  const orgSessions = overview.visibility.ga4_organic.current.organic_sessions;
  const orgDelta = overview.visibility.ga4_organic.delta_pct.sessions;
  const eng = overview.engagement.ga4.current;
  const engagedCount = Math.round(eng.sessions * eng.engagement_rate_pct / 100);
  const engagedDelta = overview.engagement.ga4.delta_pct.engagement_rate;
  const conversions = overview.conversion.ga4.current.conversions;
  const convDelta = overview.conversion.ga4.delta_pct.conversions;
  const geoAvailable = overview.geo.available;
  const geoPct = geoAvailable ? overview.geo.score_pct : null;
  const geoPrev = geoAvailable ? overview.geo.previous_month_pct : null;
  const geoDelta = (geoPct != null && geoPrev != null)
    ? ((geoPct - geoPrev) / Math.max(geoPrev, 0.1)) * 100
    : null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Your marketing — {periodLabel}</h1>
          <div className="subtle">
            Compared to {new Date(overview.prior_period.start).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </div>
        </div>
      </div>

      <section className="tiles">
        <MetricTile
          label="People who found you"
          value={formatNumber(orgSessions)}
          deltaPct={orgDelta}
          footnote="Visits from Google search"
        />
        <MetricTile
          label="Engaged visitors"
          value={formatNumber(engagedCount)}
          deltaPct={engagedDelta}
          footnote={`${eng.engagement_rate_pct.toFixed(0)}% of visitors actively engaged`}
        />
        <MetricTile
          label="Inquiries this month"
          value={formatNumber(conversions)}
          deltaPct={convDelta}
          footnote={conversions === 0 ? "Tracking coming soon" : "Form fills & calls"}
        />
        <MetricTile
          label="AI visibility"
          value={geoPct != null ? `${geoPct.toFixed(0)}%` : "—"}
          deltaPct={geoDelta}
          footnote="How often AI engines mention you"
        />
      </section>

      <section className="grid grid-2" style={{ marginTop: 20 }}>
        <div className="card">
          <h2>Visitors over time</h2>
          <MetricChart series={visSeries} height={220} />
        </div>
        <div className="card">
          <h2>Where visitors come from</h2>
          <DeviceBreakdown rows={overview.engagement.by_device_current} mode="client" />
        </div>
      </section>

      <section style={{ marginTop: 20 }}>
        <div className="card">
          <h2>Your most-visited pages</h2>
          <TopPagesList rows={overview.engagement.top_landing_pages_current?.slice(0, 5)} />
        </div>
      </section>

      {narrative && (
        <section style={{ marginTop: 20 }}>
          <div className="card">
            {narrative.headline && (
              <p className="headline" style={{ margin: 0, fontSize: 18 }}>{narrative.headline}</p>
            )}
            <details className="disclosure">
              <summary>Read the full story of this month</summary>
              <NarrativeReader narrative={narrative.client_narrative} />
            </details>
          </div>
        </section>
      )}
    </div>
  );
}

function TopPagesList({ rows }) {
  if (!rows || rows.length === 0) return <div className="empty">No landing pages yet.</div>;
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Page</th>
          <th style={{ textAlign: "right" }}>Visits</th>
          <th style={{ textAlign: "right" }}>Engagement</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td style={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.page === "/" ? "Homepage" : r.page === "(not set)" ? "Direct / unknown" : r.page}
            </td>
            <td style={{ textAlign: "right" }}>{r.sessions.toLocaleString()}</td>
            <td style={{ textAlign: "right" }}>{r.engagement_rate_pct.toFixed(0)}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
